/**
 * React component that renders a stylized note/link list item for a Board.
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'client/shared/js/components/Link'
import MiniCardListItem from '../MiniCardListItem'
import { Title, NoteBody, BtnLink } from '../MiniCardUIComponents'
import { isLinkNote } from 'client/app/js/components/RichTextEditor'
import styles from './MiniNoteCardListItem.module.scss'

const MiniPlaceCardListItem = ({ place, header, url, leftChildren, rightChildren, onClick, onBtnClick, onSeeDetailsClick }) => {
  const isUrlOnly = useMemo(() => isLinkNote(place.notes), [place.notes])

  const icon = useMemo(() => {
    const icon = (isUrlOnly) ? (
      <FontAwesomeIcon className={cx(styles['link-icon'])} icon={['fal', 'link']} />
    ) : (
      <FontAwesomeIcon className={cx(styles['note-icon'])} icon={['fas', 'note-sticky']} />
    )

    return (
      <div className={cx(styles['icon-container'])}>
        {icon}
      </div>
    )
  }, [isUrlOnly])

  const seeMore = useMemo(() => {
    if (!onSeeDetailsClick) {
      return null
    }

    const seeMoreBtn = (
      <BtnLink title='See note details' onClick={onSeeDetailsClick}>
        See more
      </BtnLink>
    )

    return url ? (<Link to={url} anchor silent>{seeMoreBtn}</Link>) : seeMoreBtn
  }, [url, onSeeDetailsClick])

  const body = useMemo(() => {
    return (
      <NoteBody place={place} />
    )
  }, [place])

  return (
    <MiniCardListItem
      className={cx({
        [styles['note-item']]: !isUrlOnly,
        [styles['link-item']]: isUrlOnly
      })}
      leftChildren={icon}
      rightChildren={rightChildren}
      onClick={onClick}
      header={header}
      imgContainerClass={styles['img-container']}>
      <div className={cx(styles['item'])}>
        <Title>{place.name}</Title>
        {body}
        <span className={cx(styles['item-links'])}>
          {seeMore}
        </span>
      </div>
    </MiniCardListItem>
  )
}

MiniPlaceCardListItem.propTypes = {
  place: PropTypes.object.isRequired,
  header: PropTypes.node,
  url: PropTypes.string,
  leftChildren: PropTypes.node,
  rightChildren: PropTypes.node,
  onClick: PropTypes.func,
  onBtnClick: PropTypes.func,
  onSeeDetailsClick: PropTypes.func
}

MiniPlaceCardListItem.defaultProps = {
  onClick: () => false,
  onSeeDetailsClick: () => false
}

export default MiniPlaceCardListItem
